/**
 * 2.0.0: 主题切换状态管理
 *
 * 管理亮色/暗色/跟随系统三种主题模式。
 * 使用 Zustand 管理状态，localStorage 持久化用户偏好，
 * matchMedia 监听系统偏好变化。
 *
 * 核心逻辑：
 * 1. 初始化时从 localStorage 读取用户偏好（key: 'theme-mode'）
 * 2. 如果未设置或为 'system'，监听系统偏好
 * 3. 根据 resolved 值在 <html> 上添加/移除 'dark' class
 * 4. 切换时添加 theme-transitioning class 实现 300ms 过渡
 */

import { create } from 'zustand';

// 三种模式：light（强制亮色）、dark（强制暗色）、system（跟随系统）
export type ThemeMode = 'light' | 'dark' | 'system';

interface ThemeState {
  /** 用户选择的模式 */
  mode: ThemeMode;
  /** 实际生效的主题（解析 system 后的结果） */
  resolved: 'light' | 'dark';
  /** 设置主题模式，自动持久化并应用到 DOM */
  setMode: (mode: ThemeMode) => void;
  /** 初始化主题系统（App 挂载时调用一次） */
  init: () => void;
}

/**
 * 根据系统偏好检测当前应使用的主题
 * 在 SSR 环境或不支持 matchMedia 时默认返回 'light'
 */
const getSystemTheme = (): 'light' | 'dark' => {
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
};

/**
 * 解析实际主题：如果 mode 是 system 则查询系统偏好
 */
const resolveTheme = (mode: ThemeMode): 'light' | 'dark' => {
  if (mode === 'system') return getSystemTheme();
  return mode;
};

/** 2.0.7: 与 Tailwind 一致的色值，用于 CSS 变量，避免依赖 dark class 重绘导致残留 */
const THEME_VARS = {
  light: {
    appBg: 'linear-gradient(to bottom right, #fff7ed, #ffffff, #fff1f2)', // orange-50, white, red-50
    chatAreaBg: '#ffffff',
  },
  dark: {
    appBg: 'linear-gradient(to bottom right, #030712, #030712, #111827)', // gray-950, gray-950, gray-900
    chatAreaBg: '#030712', // gray-950
  },
} as const;

/**
 * 在 <html> 上设置主题相关的 CSS 变量。
 * 与 class 在同一同步块中设置，切换主题时背景不依赖浏览器对 .dark 的重绘，避免残留。
 */
const setThemeCSSVars = (resolved: 'light' | 'dark') => {
  const root = document.documentElement;
  const vars = THEME_VARS[resolved];
  root.style.setProperty('--app-bg', vars.appBg);
  root.style.setProperty('--chat-area-bg', vars.chatAreaBg);
};

/**
 * 将主题应用到 DOM
 * 添加/移除 <html> 的 dark class，并同步设置 CSS 变量（2.0.7 根布局与聊天区用变量，避免多次切换后背景残留）。
 */
const applyThemeToDOM = (resolved: 'light' | 'dark') => {
  const root = document.documentElement;

  root.classList.add('theme-transitioning');

  if (resolved === 'dark') {
    root.classList.add('dark');
  } else {
    root.classList.remove('dark');
  }

  // 与 class 同帧设置 CSS 变量，根布局和聊天区使用变量作为背景，不依赖 .dark 重绘
  setThemeCSSVars(resolved);

  void root.offsetHeight;

  setTimeout(() => {
    root.classList.remove('theme-transitioning');
  }, 300);
};

/**
 * 从 localStorage 读取用户保存的主题偏好
 * 如果无效或未设置，返回默认值 'system'（跟随系统）
 */
const getSavedMode = (): ThemeMode => {
  try {
    const saved = localStorage.getItem('theme-mode');
    if (saved === 'light' || saved === 'dark' || saved === 'system') {
      return saved;
    }
  } catch {
    // localStorage 不可用时静默失败
  }
  return 'system'; // 默认跟随系统
};

// 系统偏好变化监听器的清理函数引用
let mediaQueryCleanup: (() => void) | null = null;

/**
 * 设置系统偏好监听器
 * 当用户选择 'system' 模式时，需要监听系统主题偏好变化
 * 并实时更新 resolved 状态和 DOM
 */
const setupSystemListener = (store: { getState: () => ThemeState }) => {
  // 先清理旧的监听器
  if (mediaQueryCleanup) {
    mediaQueryCleanup();
    mediaQueryCleanup = null;
  }

  const state = store.getState();

  // 仅在 system 模式下监听
  if (state.mode !== 'system') return;

  const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

  const handler = (e: MediaQueryListEvent) => {
    const newResolved = e.matches ? 'dark' : 'light';
    // 直接更新 store 的 resolved 字段和 DOM
    useThemeStore.setState({ resolved: newResolved });
    applyThemeToDOM(newResolved);
  };

  mediaQuery.addEventListener('change', handler);

  mediaQueryCleanup = () => {
    mediaQuery.removeEventListener('change', handler);
  };
};

/**
 * 主题 Store
 *
 * 使用示例：
 * ```tsx
 * const { mode, resolved, setMode, init } = useThemeStore();
 *
 * // App 挂载时初始化
 * useEffect(() => { init(); }, [init]);
 *
 * // 切换主题
 * <button onClick={() => setMode('dark')}>暗色</button>
 *
 * // 读取当前主题
 * console.log(resolved); // 'light' | 'dark'
 * ```
 */
export const useThemeStore = create<ThemeState>((set, get, store) => {
  // 读取保存的模式
  const savedMode = getSavedMode();
  const initialResolved = resolveTheme(savedMode);

  return {
    mode: savedMode,
    resolved: initialResolved,

    setMode: (mode: ThemeMode) => {
      const resolved = resolveTheme(mode);

      // 持久化到 localStorage
      try {
        localStorage.setItem('theme-mode', mode);
      } catch {
        // 静默失败
      }

      // 更新状态
      set({ mode, resolved });

      // 应用到 DOM
      applyThemeToDOM(resolved);

      // 重新设置系统监听器
      setupSystemListener(store);
    },

    init: () => {
      const state = get();

      const root = document.documentElement;
      if (state.resolved === 'dark') {
        root.classList.add('dark');
      } else {
        root.classList.remove('dark');
      }
      setThemeCSSVars(state.resolved);

      setupSystemListener(store);
    },
  };
});
